/**
 * CrmProposalTypes - Tela de gestão de Tipos de Proposta do CRM
 *
 * Padrão visual alinhado a CrmLossReasons (header compacto + card com tabela +
 * badge de status via StatusDot).
 *
 * RELEASE: 2026-08-25 v1.0.0
 */
import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCrmProposalTypes,
  useDeleteProposalType,
  type ProposalTypeEntry,
} from '@/hooks/useCrmProposalTypes';
import { ProposalTypeFormDialog } from '@/components/crm/proposal-types/ProposalTypeFormDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

export default function CrmProposalTypes() {
  const { types, isLoading, refetch } = useCrmProposalTypes(true);
  const del = useDeleteProposalType();
  const { canCreate: pCanCreate, canEdit: pCanEdit, canDelete: pCanDelete } = usePermissions();

  const canCreate = pCanCreate('crm_param_proposal_types');
  const canEdit = pCanEdit('crm_param_proposal_types');
  const canDelete = pCanDelete('crm_param_proposal_types');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProposalTypeEntry | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProposalTypeEntry | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return types;
    return types.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        (t.description ?? '').toLowerCase().includes(term),
    );
  }, [types, search]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (t: ProposalTypeEntry) => {
    setEditing(t);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await del.mutateAsync(pendingDelete.id);
    setPendingDelete(null);
    refetch();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Tipos de Proposta</h1>
        </div>
        {canCreate && (
          <Button
            size="xs"
            onClick={openCreate}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5" />
            Novo Tipo
          </Button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar tipos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {!isLoading && filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum tipo cadastrado</p>
              <p className="text-sm mt-1">
                {search
                  ? 'Tente ajustar sua busca'
                  : 'Cadastre o primeiro tipo para classificar suas propostas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Cor</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[90px] text-center">Ordem</TableHead>
                  <TableHead className="w-[100px] text-center">Usos</TableHead>
                  <TableHead className="w-[120px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando tipos...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  filtered.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div
                          className="h-6 w-6 rounded-full ring-1 ring-border"
                          style={{ backgroundColor: t.color ?? '#9ca3af' }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm truncate max-w-[320px]">
                        {t.description || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={t.is_active ? 'emerald' : 'muted'} />
                          {t.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm">
                        {t.sort_order}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm">
                        {t.usage_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEdit(t)}
                              title="Editar"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setPendingDelete(t)}
                              title="Excluir"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <ProposalTypeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tipo de proposta?</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir o tipo <strong>{pendingDelete?.name}</strong>.
              Se houver propostas utilizando este tipo, ele será apenas desativado
              (continuará visível nas propostas existentes, mas não aparecerá em novas).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
